
// src/pages/product-module/UpdateProduct.js

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardWrapper from "../../components/DashboardWrapper";
import CrudForm from "../../components/dashboard/form/CrudForm";
import productService from "../../services/productService";

const emptyVariant = () => ({
  variant_size: "",
  color_name: "",
  unit: "",
  variant_sku: "",
  variant_price: "",
  variant_offer_price: "",
  variant_qty: "",
});

const getArray = (response) => {
  const body = response?.data ?? response;

  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;

  return [];
};
const getOptionValue = (item, keys) => {
  if (typeof item === "string" || typeof item === "number") {
    return String(item).trim();
  }

  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      const value = String(item[key]).trim();

      if (value) return value;
    }
  }

  return "";
};

const COLOR_MAP = {
  Red: "#FF0000",
  Orange: "#FFA500",
  Green: "#008000",
  Blue: "#0000FF",
  Purple: "#800080",
  Pink: "#FFC0CB",
  Brown: "#A52A2A",
  White: "#FFFFFF",
  Gray: "#808080",
  Black: "#000000",
  Golden: "#FFD700",
  Silver: "#C0C0C0",
  Yellow: "#FFFF00",
  "Mint Green": "#98FF98",
  "Mint Pink": "#FFD1DC",
};

const allowedFields = [
  "name",
  "images",
  "category_id",
  "sub_category_id",
  "child_category_id",
  "brand_id",
  // "qty",
  "short_description",
  "long_description",
  "video_link",
  // "sku",
  // "price",
  // "offer_price",
  "offer_start_date",
  "offer_end_date",
  "product_type",
  "status",
  "seo_title",
  "seo_description",
  "is_approved",
  "colors",
];

export default function UpdateProduct() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
const [unitOptions, setUnitOptions] = useState([]);

const [categories, setCategories] = useState([]);
const [subCategories, setSubCategories] = useState([]);
const [childCategories, setChildCategories] = useState([]);
const [brands, setBrands] = useState([]);

const [sizeOptions, setSizeOptions] = useState([]);
const [colorOptions, setColorOptions] = useState([]);

const [variants, setVariants] = useState([emptyVariant()]);

  // Load the latest product details from the API.
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);

      try {
        

const results = await Promise.allSettled([
  productService.getProductById(id),
  productService.getAllCategories(),
  productService.getAllSubCategories(),
  productService.getAllChildCategories(),
  productService.getAllBrands(),
  productService.getAllProducts(), // added
  productService.getAllColors(),
]);

        if (!mounted) return;

       

const [
  productResult,
  categoryResult,
  subCategoryResult,
  childCategoryResult,
  brandResult,
  allProductsResult, // added
  colorResult,
] = results;

        if (productResult.status !== "fulfilled") {
          throw productResult.reason;
        }

        const response = productResult.value;
        const product =
          response?.data?.data ??
          response?.data?.product ??
          response?.data;

        if (!product || typeof product !== "object") {
          throw new Error("Product details were not found.");
        }

        // Load dropdown options.
        const readOptions = (result, label) => {
          if (result.status === "fulfilled") {
            return getArray(result.value);
          }

          console.error(`Failed to load ${label}:`, result.reason);
          return [];
        };

        setCategories(readOptions(categoryResult, "categories"));
        setSubCategories(
          readOptions(subCategoryResult, "subcategories")
        );
        setChildCategories(
          readOptions(childCategoryResult, "child categories")
        );

        setBrands(
          readOptions(brandResult, "brands").map((brand) => ({
            id: brand.id,
            name: brand.name,
          }))
        );
const savedVariantItems = Array.isArray(product.variant_items)
  ? product.variant_items
  : []; 
const allProducts = readOptions(allProductsResult, "products");

const sizesSet = new Set();
const unitsSet = new Set();

allProducts.forEach((product) => {
  const items = Array.isArray(product?.variant_items)
    ? product.variant_items
    : [];

  items.forEach((item) => {
    const size = String(item?.variant_size ?? "").trim();
    const unit = String(item?.unit ?? "").trim();

    if (size) sizesSet.add(size);
    if (unit) unitsSet.add(unit);
  });
});

// Include this product's saved values too, even if they aren't in allProducts.
savedVariantItems.forEach((item) => {
  const size = String(item?.variant_size ?? "").trim();
  const unit = String(item?.unit ?? "").trim();

  if (size) sizesSet.add(size);
  if (unit) unitsSet.add(unit);
});

const allSizes = [...sizesSet].sort((a, b) =>
  a.localeCompare(b, undefined, { numeric: true })
);

const allUnits = [...unitsSet].sort((a, b) => a.localeCompare(b));

setSizeOptions(allSizes);
setUnitOptions(allUnits);

console.log("Update form size options:", allSizes);
console.log("Update form unit options:", allUnits);

console.log("Saved variant items:", savedVariantItems);
console.log("Size dropdown options:", allSizes);
console.log("Unit dropdown options:", allUnits); 
        // Normalize saved colors.
        const normalizedColors = Array.isArray(product.colors)
          ? product.colors.map((color) => {
              const code =
                typeof color === "string"
                  ? color
                  : color?.color_code || color?.code || "";

              const matchedName =
                Object.keys(COLOR_MAP).find(
                  (name) =>
                    COLOR_MAP[name].toLowerCase() ===
                    String(code).toLowerCase()
                ) ||
                (typeof color === "object" ? color?.name : "") ||
                "";

              return {
                name: matchedName,
                code,
              };
            })
          : [];

        // Normalize existing thumbnail and gallery images.
        const thumbnail = product.thumb_image
          ? [
              {
                id: null,
                image: product.thumb_image,
                existing: true,
                isThumb: true,
              },
            ]
          : [];

        const gallery = Array.isArray(
          product.product_image_galleries
        )
          ? product.product_image_galleries.map((image) => ({
              id: image.id,
              image: image.image,
              existing: true,
              isThumb: false,
            }))
          : [];

        // Prepare the form values.
        const normalized = {};

        allowedFields.forEach((field) => {
          normalized[field] =
            product[field] ??
            (field === "status"
              ? 1
              : field === "is_approved"
                ? 0
                : "");
        });

      // Combine thumbnail and gallery, removing duplicate image paths.
const allImages = [...thumbnail, ...gallery];

normalized.images = allImages.filter(
  (image, index, images) =>
    images.findIndex(
      (item) => String(item.image) === String(image.image)
    ) === index
);
        normalized.colors = normalizedColors;
        normalized.status =
          product.status === true ||
          product.status === 1 ||
          product.status === "1"
            ? 1
            : 0;
        normalized.is_approved = Number(
          product.is_approved ?? 0
        );

        setInitialData(normalized);

        // Restore saved variants.

 // Restore variants from the product details API only.


const savedVariants = savedVariantItems.map((item) => {
  const rawSize =
    item?.variant_size ??
    item?.size?.name ??
    item?.size?.size ??
    item?.size ??
    item?.size_name ??
    "";

  return {
    id: item.id,
    variant_size: String(rawSize).trim(),
    color_name: String(
      item.color_name ??
        item.color?.name ??
        item.color?.color_name ??
        item.color ??
        ""
    ),
    unit: String(item.unit ?? ""),
    variant_sku: String(item.variant_sku ?? ""),
    variant_price: String(item.variant_price ?? ""),
    variant_offer_price: String(item.variant_offer_price ?? ""),
    variant_qty: String(item.variant_qty ?? ""),
  };
});

setVariants(
  savedVariants.length
    ? savedVariants.map((variant) => ({
        ...emptyVariant(),
        ...variant,
        variant_size: String(variant.variant_size ?? "").trim(),
        unit: String(variant.unit ?? "").trim(),
      }))
    : [emptyVariant()]
);



console.log("Loaded product variants:", savedVariants);

console.log("Loaded size options:", sizeOptions);
console.log("Loaded color options:", colorOptions);
console.log("Loaded product variants:", savedVariants);

        console.log("Loaded product:", product);
        console.log("Loaded variants:", savedVariants);
      } catch (error) {
        console.error(
          "Failed to load product:",
          error?.response?.data || error
        );

        if (mounted) {
          await Swal.fire(
            "Error",
            "Failed to load product details. Please try again.",
            "error"
          );

          navigate("/admin/all-products");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [id, navigate]);

  // Submit the updated product.
  const handleSubmit = useCallback(
    async (formData) => {
      try {
        if (!String(formData.name ?? "").trim()) {
          await Swal.fire(
            "Validation",
            "Product name is required.",
            "warning"
          );
          return;
        }

        const validVariants = variants.filter((variant) =>
          Object.values(variant).some(
            (value) => String(value ?? "").trim() !== ""
          )
        );

        if (validVariants.length === 0) {
          await Swal.fire(
            "Validation",
            "Please add at least one product variant.",
            "warning"
          );
          return;
        }

        const incompleteVariant = validVariants.some((variant) => {
      const requiredFields = [
  variant.variant_size,
  // variant.color_name,
  variant.unit,
  variant.variant_sku,
  variant.variant_price,
  variant.variant_qty,
];

          const price = Number(variant.variant_price);
          const quantity = Number(variant.variant_qty);

          const invalidOfferPrice =
            String(variant.variant_offer_price ?? "").trim() !== "" &&
            (
              !Number.isFinite(
                Number(variant.variant_offer_price)
              ) ||
              Number(variant.variant_offer_price) < 0
            );

          return (
            requiredFields.some(
              (value) => String(value ?? "").trim() === ""
            ) ||
            !Number.isFinite(price) ||
            price < 0 ||
            !Number.isFinite(quantity) ||
            quantity < 0 ||
            invalidOfferPrice
          );
        });

        if (incompleteVariant) {
          await Swal.fire(
            "Validation",
            "Please complete Size, Unit, SKU, Price and Quantity for every variant.",
            "warning"
          );
          return;
        }

        const skus = validVariants.map((variant) =>
          variant.variant_sku.trim().toLowerCase()
        );

        if (new Set(skus).size !== skus.length) {
          await Swal.fire(
            "Validation",
            "Each variant must have a unique SKU.",
            "warning"
          );
          return;
        }

        const payload = new FormData();

        const slug = String(formData.name ?? "")
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "-");

        // Append regular product fields.
      
Object.entries(formData).forEach(([key, value]) => {
  if (
    key === "images" ||
    key === "colors" ||
    key === "variant_items" ||
    key === "products"
  ) {
    return;
  }

  if (value === undefined || value === null) {
    return;
  }

  payload.append(
    key,
    typeof value === "object"
      ? JSON.stringify(value)
      : String(value)
  );
});

        payload.set("name", String(formData.name).trim());
        payload.set("slug", slug);
        payload.set("status", String(Number(formData.status ?? 1)));
        payload.set(
          "is_approved",
          String(Number(formData.is_approved ?? 0))
        );

        // Laravel method override.
        payload.append("_method", "PUT");

        // Send selected colors.
        if (Array.isArray(formData.colors)) {
          formData.colors.forEach((color) => {
            const colorCode =
              typeof color === "string"
                ? color
                : color?.code || color?.color_code || "";

            if (colorCode) {
              payload.append("colors[]", colorCode);
            }
          });
        }

        // Send newly selected images and preserve existing gallery images.
        const images = Array.isArray(formData.images)
          ? formData.images
          : [];

        images.forEach((image) => {
          if (image instanceof File) {
            payload.append("images[]", image);
          } else if (image?.existing && image?.id) {
            payload.append(
              "existing_image_ids[]",
              String(image.id)
            );
          }
        });

        // Submit the product variants.
        // The Laravel update endpoint must accept this nested structure.
     
const variantFields = [
  "id",
  "variant_size",
  "color_name",
  "unit",
  "variant_sku",
  "variant_price",
  "variant_offer_price",
  "variant_qty",
];

validVariants.forEach((variant, index) => {
  variantFields.forEach((key) => {
    const value = variant[key];

    if (value !== undefined && value !== null) {
      payload.append(
        `variant_items[${index}][${key}]`,
        String(value)
      );
    }
  });
});

        console.log("Updating product:", id);

        for (const [key, value] of payload.entries()) {
          console.log(
            key,
            value instanceof File ? value.name : value
          );
        }

        await productService.updateProductWithFile(id, payload);

        await Swal.fire({
          title: "Success",
          text: "Product updated successfully!",
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "swal-confirm-btn",
          },
          didOpen: () => {
            if (!document.getElementById("swal-button-style")) {
              const style = document.createElement("style");
              style.id = "swal-button-style";
              style.innerHTML = `
                .swal-confirm-btn {
                  background-color: var(--color-text-green) !important;
                  color: #fff !important;
                  border: none !important;
                  border-radius: 4px !important;
                  padding: 8px 20px !important;
                  font-weight: bold !important;
                }
              `;
              document.head.appendChild(style);
            }
          },
        });

        navigate("/admin/all-products");
      } catch (error) {
        console.error(
          "Update product failed:",
          error?.response?.data || error
        );

        const errors = error?.response?.data?.errors || {};
        const firstError =
          Object.values(errors).flat()[0] ||
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Product update failed.";

        await Swal.fire("Error", String(firstError), "error");
      }
    },
    [id, navigate, variants]
  );

  if (loading) {
    return (
      <DashboardWrapper>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardWrapper>
    );
  }

  if (!initialData) {
    return (
      <DashboardWrapper>
        <div className="alert alert-danger">
          Product data not found.
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
            {/* <button
            type="button"
            className="btn btn-secondary"
            onClick={() => ("/admin/all-products")}
          >
            Back
          </button> */}
          {/* <h1 className="h4 mb-0">Update Product</h1> */}

        
        </div>

        <div className="card">
          <h5 className="card-header">Edit Product</h5>

          <div className="card-body">
           
<CrudForm
  key={id}
  initialData={initialData}
  onSubmit={handleSubmit}
  allowedFields={allowedFields}
  categories={categories}
  subCategories={subCategories}
  childCategories={childCategories}
  brands={brands}
  sizeOptions={sizeOptions}
  colorOptions={colorOptions}
  unitOptions={unitOptions}
  variants={variants}
  onVariantsChange={setVariants}
  showVariants={true}
  fileUploadStyle="product"
  fileFields={[{ name: "images", multiple: true }]}
/>
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
